import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { customerAPI, interactionAPI, assetAPI, subscriptionAPI } from "../lib/api";
import { formatDate, formatDateTime, formatCurrency, getStatusColor } from "../lib/utils";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  MessageSquare,
  Package,
  Calendar,
  Clock
} from "lucide-react";
import { toast } from "sonner";

export function CustomerDetails() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const loadCustomerData = useCallback(async () => {
    try {
      setLoading(true);
      const [customerRes, interactionsRes, assetsRes, subscriptionsRes] = await Promise.all([
        customerAPI.getById(id),
        interactionAPI.getByCustomer(id),
        assetAPI.getByCustomer(id),
        subscriptionAPI.getByCustomer(id)
      ]);
      
      setCustomer(customerRes.data);
      setInteractions(interactionsRes.data);
      setAssets(assetsRes.data);
      setSubscriptions(subscriptionsRes.data);
    } catch (error) {
      toast.error("Failed to load customer data");
      console.error("Load customer data error:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadCustomerData();
    }
  }, [id, loadCustomerData]);

  const getInteractionTypeColor = (type) => {
    const colors = {
      phone_call: "bg-blue-100 text-blue-800",
      email: "bg-green-100 text-green-800",
      meeting: "bg-purple-100 text-purple-800",
      support: "bg-orange-100 text-orange-800",
      sales: "bg-cyan-100 text-cyan-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Customer not found</h2>
        <Link to="/customers">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Customers
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/customers">
          <Button variant="outline" size="sm" data-testid="back-to-customers-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Link>
        
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="customer-details-title">
            {customer.name}
          </h1>
          {customer.company && (
            <p className="text-slate-600 mt-1">{customer.company}</p>
          )}
        </div>
      </div>

      {/* Customer Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="w-5 h-5 mr-2" />
            Customer Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="customer-info">
            <div className="space-y-4">
              {customer.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-medium">{customer.email}</p>
                  </div>
                </div>
              )}
              
              {customer.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Phone</p>
                    <p className="font-medium">{customer.phone}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {customer.company && (
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Company</p>
                    <p className="font-medium">{customer.company}</p>
                  </div>
                </div>
              )}
              
              {customer.address && (
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-600">Address</p>
                    <p className="font-medium">{customer.address}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-600">Customer since</p>
                  <p className="font-medium">{formatDate(customer.created_date)}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="interactions" data-testid="interactions-tab">Interactions ({interactions.length})</TabsTrigger>
          <TabsTrigger value="assets" data-testid="assets-tab">Assets ({assets.length})</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="subscriptions-tab">Subscriptions ({subscriptions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Interactions</p>
                    <p className="text-2xl font-bold text-slate-900">{interactions.length}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Registered Assets</p>
                    <p className="text-2xl font-bold text-slate-900">{assets.length}</p>
                  </div>
                  <Package className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Active Subscriptions</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {subscriptions.filter(s => s.status === 'active').length}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="interactions">
          {interactions.length > 0 ? (
            <div className="space-y-4" data-testid="interactions-list">
              {interactions.map((interaction) => (
                <Card key={interaction.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Badge className={getInteractionTypeColor(interaction.type)}>
                            {interaction.type.replace('_', ' ')}
                          </Badge>
                          {interaction.duration_minutes && (
                            <span className="text-sm text-slate-600">
                              {interaction.duration_minutes} minutes
                            </span>
                          )}
                        </div>
                        {interaction.notes && (
                          <p className="text-slate-700 mb-2">{interaction.notes}</p>
                        )}
                        <p className="text-sm text-slate-500">
                          {formatDateTime(interaction.date)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-16">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No interactions yet</h3>
                <p className="text-slate-600 mb-6">Start logging interactions with this customer</p>
                <Link to="/interactions">
                  <Button>Add Interaction</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="assets">
          {assets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="assets-list">
              {assets.map((asset) => (
                <Card key={asset.id}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-2">{asset.name}</h3>
                    {asset.serial_number && (
                      <p className="text-sm text-slate-600 mb-1">
                        Serial: {asset.serial_number}
                      </p>
                    )}
                    {asset.purchase_date && (
                      <p className="text-sm text-slate-600 mb-2">
                        Purchased: {formatDate(asset.purchase_date)}
                      </p>
                    )}
                    {asset.details && (
                      <p className="text-slate-700">{asset.details}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-16">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No assets registered</h3>
                <p className="text-slate-600 mb-6">Register assets for this customer</p>
                <Link to="/assets">
                  <Button>Add Asset</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="subscriptions">
          {subscriptions.length > 0 ? (
            <div className="space-y-4" data-testid="subscriptions-list">
              {subscriptions.map((subscription) => (
                <Card key={subscription.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-slate-900">{subscription.service_name}</h3>
                          <Badge className={getStatusColor(subscription.status)}>
                            {subscription.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
                          <div>
                            <span className="font-medium">Start Date:</span> {formatDate(subscription.start_date)}
                          </div>
                          <div>
                            <span className="font-medium">End Date:</span> {formatDate(subscription.end_date)}
                          </div>
                          {subscription.renewal_reminder_date && (
                            <div>
                              <span className="font-medium">Renewal Reminder:</span> {formatDate(subscription.renewal_reminder_date)}
                            </div>
                          )}
                          {subscription.price && (
                            <div>
                              <span className="font-medium">Price:</span> {formatCurrency(subscription.price)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-16">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No subscriptions</h3>
                <p className="text-slate-600 mb-6">Add subscriptions for this customer</p>
                <Link to="/subscriptions">
                  <Button>Add Subscription</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
